import { useEffect, useMemo, useState } from 'react';

interface UsePaginationResult<T> {
  page: number;
  setPage: (page: number) => void;
  pageCount: number;
  pageItems: T[];
  totalItems: number;
}

// Paginates an already-filtered/sorted array client-side. Resets back to
// page 1 whenever the underlying item count changes (e.g. a search/filter
// narrows the list) so the user is never stranded on an empty page.
export function usePagination<T>(items: T[], pageSize = 20): UsePaginationResult<T> {
  const [page, setPage] = useState(1);
  const totalItems = items.length;
  const pageCount = Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => {
    setPage(1);
  }, [totalItems]);

  const clampedPage = Math.min(page, pageCount);

  const pageItems = useMemo(() => {
    const start = (clampedPage - 1) * pageSize;
    return items.slice(start, start + pageSize);
  }, [items, clampedPage, pageSize]);

  return { page: clampedPage, setPage, pageCount, pageItems, totalItems };
}
